// Overlay sheets: settings, a stage card, and the list of place ideas.
import { useMemo, useSyncExternalStore } from 'react'
import { subscribeVoices, voiceReady } from '../audio/tts'
import type { Person } from '../../shared/events'
import { useChildSettings } from '../data/controls'
import { record } from '../data/events'
import { useEvents } from '../data/progress'
import { describeRules } from '../domain/controls'
import { myPlaces } from '../domain/places'
import { isSolo } from '../domain/players'
import type { Pack } from '../../shared/pack/schema'
import { closeAllOverlays, goTab, openOverlay } from '../app/router'
import { Sheet } from '../app/Sheet'
import { SyncPill } from '../app/StatusBits'
import { usePlaceAudioPrefetch } from '../audio/placePrefetch'
import { OrganiserIcon, PlayerFace } from '../art/icons'
import { useSession } from '../data/session'
import { useSettings, type MotionChoice } from '../data/settings'
import { syncNow } from '../data/sync'
import { daysBetween } from '../domain/time'
import { fmt, t } from '../strings/en'
import { LegChip, legsAround } from './TripScreen'
import { NotifyPanel } from './NotifyPanel'
import { When } from './When'

function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange(v: boolean): void }) {
  return <button className="toggle" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} />
}

/** How far the trip's island sounds are cached for offline (WI-24); nothing before it starts. */
function IslandSoundsLine() {
  const { total, done, running } = usePlaceAudioPrefetch()
  if (!total) return null
  return (
    <p className="caption" role="status">
      {done >= total ? t.settings.soundsReady : running ? t.settings.soundsLoading(done, total) : t.settings.soundsPartial(done, total)}
    </p>
  )
}

/** WI-26: the phone has no voice for the trip's language (cards without a recording are read instead). */
function NoVoiceLine() {
  const ready = useSyncExternalStore(subscribeVoices, voiceReady)
  if (ready) return null
  return (
    <p className="caption" role="status">
      {t.settings.noVoice}
    </p>
  )
}

export function SettingsSheet({ pack, person }: { pack: Pack; person: Person }) {
  const s = useSettings()
  const { renew, returnTo, people, backToChild } = useSession()
  const child = returnTo ? people?.find((p) => p.id === returnTo) : undefined
  // Just me (WI-37): nobody to switch to, and "family" wording becomes "my".
  const solo = isSolo(people)
  return (
    <Sheet title={t.settings.title}>
      {child && person.kind !== 'child' ? (
        <div className="panel sheet-panel parent-mode">
          <div className="row">
            <div>
              <b>{t.settings.parentMode(child.name)}</b>
              <small>{t.settings.parentModeSub}</small>
            </div>
            <button
              className="btn y sm press"
              onClick={() => {
                closeAllOverlays()
                void backToChild()
              }}
            >
              {t.settings.parentModeDone}
            </button>
          </div>
        </div>
      ) : null}
      <div className="panel sheet-panel">
        <div className="row">
          <span className={`me static ${person.role}`}>{person.role === 'organiser' ? <OrganiserIcon size={24} /> : <PlayerFace size={24} />}</span>
          <div>
            <b>{person.name}</b>
            <small>{t.settings.onPhone(person.role)}</small>
          </div>
          {solo ? null : (
            <button
              className="btn w sm press"
              // The Switch sheet (WI-17, screens/SwitchSheet), pushed over Settings (WI-18): ✕ and Back come
              // back to Settings with nobody signed out; picking someone else switches and closes
              // every overlay, so Settings doesn't reopen over the next person's app.
              onClick={() => openOverlay('who')}
            >
              {t.settings.switch}
            </button>
          )}
        </div>
        <div className="row">
          <SyncPill always />
          <button className="btn w sm press" onClick={() => void syncNow({ renew, personId: () => person.id })}>
            {t.settings.syncNow}
          </button>
        </div>
      </div>
      <div className="panel sheet-panel">
        <div className="row">
          <div>
            <b>{t.settings.glove}</b>
            <small>{t.settings.gloveSub}</small>
          </div>
          <Toggle on={s.glove} label={t.settings.glove} onChange={(v) => s.set('glove', v)} />
        </div>
        <div className="row">
          <b>{t.settings.sound}</b>
          <Toggle on={s.sound} label={t.settings.sound} onChange={(v) => s.set('sound', v)} />
        </div>
        <div className="row">
          <b>{t.settings.vibration}</b>
          <Toggle on={s.vibration} label={t.settings.vibration} onChange={(v) => s.set('vibration', v)} />
        </div>
        {/* WI-40 #2: the choices on their own line, under the label, so none is cut off at 360 px. */}
        <div className="row stacked">
          <div>
            <b>{t.settings.motion}</b>
            <small>{t.settings.motionSub}</small>
          </div>
          <div className="seg" role="group" aria-label={t.settings.motion}>
            {(['auto', 'calm', 'still'] as MotionChoice[]).map((m) => (
              <button key={m} aria-pressed={s.motion === m} onClick={() => s.set('motion', m)}>
                {t.settings.motionChoices[m]}
              </button>
            ))}
          </div>
        </div>
      </div>
      <NotifyPanel person={person} />
      <button
        className="panel sheet-panel row row-btn press org-open"
        onClick={() => goTab('install')}
      >
        <div>
          <b>{t.info.settingsRow}</b>
          <small>{t.info.settingsRowSub}</small>
        </div>
        <span className="chev">›</span>
      </button>
      {person.kind === 'child' ? (
        <button className="panel sheet-panel row row-btn press org-open" onClick={() => openOverlay('rules')}>
          <div>
            <b>{t.rules.open}</b>
            <small>{t.rules.openSub}</small>
          </div>
          <span className="chev">›</span>
        </button>
      ) : null}
      {/* B15 tidy: the organiser rows (tools, itinerary, PIN, family) live on Organiser home now;
          Settings keeps this phone's own things and one way there. */}
      {person.kind !== 'child' ? (
        <button className="panel sheet-panel row row-btn press org-open" onClick={() => goTab('organiser')}>
          <div>
            <b>{t.organiser.open}</b>
            <small>{solo ? t.organiser.openSubSolo : t.organiser.openSub}</small>
          </div>
          <span className="chev">›</span>
        </button>
      ) : null}
      <details className="panel sheet-panel credits">
        <summary className="row row-btn press">
          <div>
            <b>{t.settings.credits}</b>
            <small>{t.settings.creditsSub}</small>
          </div>
          <span className="chev">›</span>
        </summary>
        {t.settings.creditLines.map((line) => (
          <div className="row" key={line}>
            <small>{line}</small>
          </div>
        ))}
      </details>
      <p className="caption">{t.settings.pack(pack.meta.title, pack.meta.version)}</p>
      <IslandSoundsLine />
      <NoVoiceLine />
    </Sheet>
  )
}

/** A child's own rules, read-only (B10): what's set for them, and what a grown-up can see. */
export function RulesSheet({ person }: { person: Person }) {
  const settings = useChildSettings(person)
  const people = useSession((s) => s.people)
  const grownUp = people?.find((p) => p.kind === 'admin')?.name ?? t.plays.grownUp
  if (!settings) return null
  return (
    <Sheet title={t.rules.title}>
      <div className="panel sheet-panel">
        {describeRules(settings).map((line) => (
          <div className="row" key={line}>
            <b>{line}</b>
          </div>
        ))}
      </div>
      <p className="mono sheet-sub">{t.rules.seeTitle(grownUp)}</p>
      <div className="panel sheet-panel">
        <div className="row">
          <small>{t.rules.see}</small>
        </div>
      </div>
      <p className="caption">{t.rules.openSub}</p>
    </Sheet>
  )
}

export function StageSheet({ pack, stageId }: { pack: Pack; stageId: string }) {
  const i = pack.itinerary.stages.findIndex((s) => s.id === stageId)
  const stage = pack.itinerary.stages[i]
  if (!stage) return null
  const legs = legsAround(pack).before.get(stage.id) ?? []
  const places = pack.places.filter((p) => p.regionId === stage.regionId)
  return (
    <Sheet title={stage.name}>
      <p className="mono sheet-sub">
        <When parts={t.trip.stageWhen(stage.startDate, stage.endDate, daysBetween(stage.startDate, stage.endDate) + 1)} />
      </p>
      {legs.length ? <div className="legs">{legs.map((l) => <LegChip key={l.id} leg={l} />)}</div> : null}
      <div className="panel sheet-panel">
        {places.slice(0, 8).map((p) => (
          <div className="row" key={p.id}>
            <div>
              <b>
                {p.name} {p.nameJa ? <span className="jp muted">{p.nameJa}</span> : null}
              </b>
              <small>{p.summary}</small>
            </div>
          </div>
        ))}
      </div>
      <p className="caption">{fmt.plural(places.length, 'idea')} around here</p>
    </Sheet>
  )
}

/** The kit's ideas; a star puts one on your want list (F4), unless a child's controls say no. */
export function IdeasSheet({ pack, person }: { pack: Pack; person: Person }) {
  const events = useEvents()
  const wanted = useMemo(() => myPlaces(events, person.id).wanted, [events, person.id])
  const settings = useChildSettings(person.kind === 'child' ? person : null)
  const canWant = settings?.canSuggest ?? true
  return (
    <Sheet title={t.crate.ideas(pack.places.length)}>
      <div className="panel sheet-panel">
        {pack.places.map((p) => (
          <div className="row" key={p.id}>
            <div>
              <b>
                {p.name} {p.nameJa ? <span className="jp muted">{p.nameJa}</span> : null}
              </b>
              <small>{p.summary}</small>
            </div>
            {canWant ? (
              <button
                className={`star press${wanted.has(p.id) ? ' on' : ''}`}
                role="switch"
                aria-checked={wanted.has(p.id)}
                aria-label={t.want.star(p.name)}
                onClick={() => void record(wanted.has(p.id) ? 'place.unwanted' : 'place.wanted', { placeId: p.id }, person.id)}
              >
                {wanted.has(p.id) ? '★' : '☆'}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </Sheet>
  )
}
